import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import Tiptap, { TiptapRef } from "@/components/Tiptap";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import { EventSkeleton } from "./EventSkeleton";
import {
  getUserAttendingEvents,
  getUserEvents,
  getRecommendedEvents,
  sharePost,
} from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import { TimeInput } from "@/components/ui/time-input";
import ShadCNDatePicker from "@/components/ui/datePicker";
import { useSelector } from "react-redux";
import { AppState } from "@/store";
import { usePosthog } from "@/hooks/usePosthog";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  X,
  Edit2,
  UserCheck,
  UserMinus,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Share2,
  Rss,
  Send,
  Flag,
  AlertTriangle,
  EyeOff,
  ExternalLink,
} from "lucide-react";
import { LinkedinIcon as Linkedin } from "@/components/icons/LinkedinIcon";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PostData } from "./Post";
import { ReportDialog } from "./ReportDialog";
import { getCommunityPath } from "@/utils/community";

export interface EventData {
  isEdited?: boolean;
  isReported?: boolean;
  id: string;
  title: string;
  date: Date;
  endDate?: Date;
  location: string;
  maxParticipants?: number;
  description?: string;
  createdBy: {
    userId: string;
    userName: string;
    userTitle: string;
    userType?: string;
    organizationId?: string;
  };
  participants: Array<{
    userId: string;
    userName: string;
    userTitle: string;
    userType?: string;
    organizationId?: string;
    photoUrl?: string;
    registeredAt: Date;
  }>;
  status: "active" | "cancelled";
}

interface EventsPageProps {
  feedPosts?: PostData[];
  onEditEvent?: (postId: string, eventDetails: any) => void;
  onCancelEvent?: (postId: string) => void;
  onLeaveEvent?: (postId: string) => void;
  onJoinEvent?: (postId: string) => void;
}

export function EventsPage({
  feedPosts = [],
  onEditEvent,
  onCancelEvent,
  onLeaveEvent,
  onJoinEvent,
}: EventsPageProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { trackEvent } = usePosthog();
  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const [events, setEvents] = useState<EventData[]>([]);
  const [attendingEventsFromAPI, setAttendingEventsFromAPI] = useState<EventData[]>([]);
  const [recommended, setRecommended] = useState<EventData[]>([]);
  const [isLoadingMyEvents, setIsLoadingMyEvents] = useState(true);
  const [isLoadingAttending, setIsLoadingAttending] = useState(true);
  const [isLoadingRecommended, setIsLoadingRecommended] = useState(true);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [cancelEventId, setCancelEventId] = useState<string | null>(null);
  const [unregisterEventId, setUnregisterEventId] = useState<string | null>(null);
  const [shareMenuOpenEventId, setShareMenuOpenEventId] = useState<string | null>(null);
  const [reportEventId, setReportEventId] = useState<string | null>(null);
  const editDescriptionEditorRef = useRef<TiptapRef>(null);

  // Handle user profile click - navigate to their profile
  const handleUserProfileClick = (userId: string, userType?: string, organizationId?: string) => {
    const currentUserType = user?.type;

    // Determine the navigation path based on user types
    if (currentUserType === "job_seeker") {
      if (userType === "job_seeker") {
        // Jobseeker viewing jobseeker - use userId
        navigate(`/jobseeker/users/${userId}`);
      } else if (userType === "employer") {
        // Jobseeker viewing employer - use organization_id if available
        if (organizationId) {
          navigate(`/jobseeker/employers/${organizationId}`);
        }
      }
    } else if (currentUserType === "employer") {
      if (userType === "employer") {
        // Employer viewing organization - use organization_id if available
        if (organizationId) {
          navigate(`/employer/organizations/${organizationId}`);
        }
      } else if (userType === "job_seeker") {
        // Employer viewing job seeker - use userId
        navigate(`/employer/jobseekers/${userId}`);
      }
    }
  };

  // Fetch user's created events from API
  useEffect(() => {
    const fetchEvents = async () => {
      if (!user?.id) {
        setIsLoadingMyEvents(false);
        return;
      }

      try {
        setIsLoadingMyEvents(true);
        const data = await getUserEvents(user.id, { page: 1, limit: 20 });

        // Transform API data to EventData format
        const transformedEvents: EventData[] = data.posts
          .filter((post) => post.type === "event" && post.event_details)
          .map((post) => ({
            id: post.id,
            title: post.event_details!.title,
            date: new Date(post.event_details!.date),
            endDate: post.event_details!.end_date
              ? new Date(post.event_details!.end_date)
              : undefined,
            location: post.event_details!.location,
            maxParticipants: post.event_details!.max_participants,
            description: post.event_details!.description,
            createdBy: {
              userId: post.author?.id || post.author_id || "",
              userName: post.author?.full_name || t("community.events.unknown"),
              userTitle:
                post.author?.job_title ||
                (post.author?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: post.author?.type,
              organizationId: post.author?.organization_id,
            },
            participants: (post.event_participants || []).map((p) => ({
              userId: p.user?.id || p.user_id || "",
              userName: p.user?.full_name || t("community.events.unknown"),
              userTitle:
                p.user?.job_title ||
                (p.user?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: p.user?.type,
              organizationId: p.user?.organization_id,
              photoUrl: p.user?.photo_thumbnail_url || p.user?.photo,
              registeredAt: new Date(p.registered_at),
            })),
            status: (post.event_status || "active") as "active" | "cancelled",
            isEdited:
              post.edit_history && Array.isArray(post.edit_history) && post.edit_history.length > 0,
            isReported: post.is_flagged === true,
          }));

        setEvents(transformedEvents);
      } catch (err) {
        console.error("Error fetching events:", err);
        toast({
          title: t("community.events.error_loading"),
          description: t("community.events.error_loading_desc"),
          variant: "destructive",
        });
      } finally {
        setIsLoadingMyEvents(false);
      }
    };

    fetchEvents();
  }, [user?.id]);

  // Fetch attending events from API
  useEffect(() => {
    const fetchAttendingEvents = async () => {
      if (!user?.id) {
        setIsLoadingAttending(false);
        return;
      }

      try {
        setIsLoadingAttending(true);
        const data = await getUserAttendingEvents(user.id, { page: 1, limit: 20 });

        const transformedEvents: EventData[] = data.posts
          .filter((post: any) => post.type === "event" && post.event_details)
          .map((post: any) => ({
            id: post.id,
            title: post.event_details!.title,
            date: new Date(post.event_details!.date),
            endDate: post.event_details!.end_date
              ? new Date(post.event_details.end_date)
              : undefined,
            location: post.event_details!.location,
            maxParticipants: post.event_details!.max_participants,
            description: post.event_details!.description,
            createdBy: {
              userId: post.author?.id || post.author_id || "",
              userName: post.author?.full_name || t("community.events.unknown"),
              userTitle:
                post.author?.job_title ||
                (post.author?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: post.author?.type,
              organizationId: post.author?.organization_id,
            },
            participants: (post.event_participants || []).map((p: any) => ({
              userId: p.user?.id || p.user_id || "",
              userName: p.user?.full_name || t("community.events.unknown"),
              userTitle:
                p.user?.job_title ||
                (p.user?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: p.user?.type,
              organizationId: p.user?.organization_id,
              photoUrl: p.user?.photo_thumbnail_url || p.user?.photo,
              registeredAt: new Date(p.registered_at),
            })),
            status: (post.event_status || "active") as "active" | "cancelled",
            isEdited:
              post.edit_history && Array.isArray(post.edit_history) && post.edit_history.length > 0,
          }));

        setAttendingEventsFromAPI(transformedEvents);
      } catch (error) {
        console.error("Error fetching attending events:", error);
      } finally {
        setIsLoadingAttending(false);
      }
    };

    fetchAttendingEvents();
  }, [user?.id]);

  // Fetch recommended events from API
  useEffect(() => {
    const fetchRecommendedEvents = async () => {
      try {
        setIsLoadingRecommended(true);
        const data = await getRecommendedEvents({ page: 1, limit: 20, order: "asc" });

        const transformedEvents: EventData[] = data.posts
          .filter((post: any) => post.type === "event" && post.event_details)
          .filter((post: any) => {
            // Exclude cancelled events
            if (post.event_status === "cancelled") return false;
            // Exclude events user created
            if (post.author?.id === user?.id || post.author_id === user?.id) return false;
            // Exclude events user is already attending
            const isAttending = post.event_participants?.some(
              (p: any) => (p.user?.id || p.user_id) === user?.id,
            );
            return !isAttending;
          })
          .map((post: any) => ({
            id: post.id,
            title: post?.event_details!.title,
            date: new Date(post?.event_details!.date),
            endDate: post?.event_details!.end_date
              ? new Date(post.event_details.end_date)
              : undefined,
            location: post?.event_details!.location,
            maxParticipants: post?.event_details!.max_participants,
            description: post?.event_details!.description,
            createdBy: {
              userId: post?.author?.id || post.author_id || "",
              userName: post?.author?.full_name || t("community.events.unknown"),
              userTitle:
                post?.author?.job_title ||
                (post?.author?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: post?.author?.type,
              organizationId: post?.author?.organization_id,
            },
            participants: (post?.event_participants || []).map((p: any) => ({
              userId: p?.user?.id || p.user_id || "",
              userName: p?.user?.full_name || t("community.events.unknown"),
              userTitle:
                p?.user?.job_title ||
                (p.user?.type === "employer"
                  ? t("community.events.employer_fallback")
                  : t("community.events.job_seeker_fallback")),
              userType: p?.user?.type,
              organizationId: p?.user?.organization_id,
              photoUrl: p?.user?.photo_thumbnail_url || p?.user?.photo,
              registeredAt: new Date(p.registered_at),
            })),
            status: (post?.event_status || "active") as "active" | "cancelled",
            isEdited:
              post?.edit_history &&
              Array.isArray(post?.edit_history) &&
              post?.edit_history.length > 0,
          }));

        setRecommended(transformedEvents);
      } catch (error) {
        console.error("Error fetching recommended events:", error);
      } finally {
        setIsLoadingRecommended(false);
      }
    };

    if (user?.id) {
      fetchRecommendedEvents();
    } else {
      setIsLoadingRecommended(false);
    }
  }, [user?.id]);

  // Edit event state
  const [editEventTitle, setEditEventTitle] = useState("");
  const [editEventDate, setEditEventDate] = useState<Date | undefined>();
  const [editEventTime, setEditEventTime] = useState("");
  const [editEventEndTime, setEditEventEndTime] = useState("");
  const [editEventTimeError, setEditEventTimeError] = useState("");
  const [editEventLocation, setEditEventLocation] = useState("");
  const [editEventMaxParticipants, setEditEventMaxParticipants] = useState("");
  const [editEventMaxParticipantsError, setEditEventMaxParticipantsError] = useState("");
  const [editEventDescription, setEditEventDescription] = useState("");

  const currentUserId = user?.id || "currentUser";

  // Note: Events are fetched directly from API via getUserEvents in useEffect above
  // We don't need to sync from feedPosts as that would overwrite the API data

  // Events state already contains only user's created events from getUserEvents API
  // No need to filter again since the API endpoint /events/user/${userId}/created returns only user's events
  const myEvents = events;

  // Split events into reported and active using is_reported from API
  const reportedEvents = myEvents.filter((event) => event.isReported === true);
  const activeEvents = myEvents.filter((event) => event.isReported !== true);

  // Use attending events from API instead of filtered local events
  const attendingEvents = attendingEventsFromAPI;

  const handleEditEvent = (event: EventData) => {
    setEditingEventId(event.id);
    setEditEventTitle(event.title);
    setEditEventDate(event.date);
    setEditEventTime(
      `${String(event.date.getHours()).padStart(2, "0")}:${String(event.date.getMinutes()).padStart(2, "0")}`,
    );
    setEditEventEndTime(
      event.endDate
        ? `${String(event.endDate.getHours()).padStart(2, "0")}:${String(event.endDate.getMinutes()).padStart(2, "0")}`
        : "",
    );
    setEditEventLocation(event.location);
    setEditEventMaxParticipants(event.maxParticipants?.toString() || "");
    setEditEventDescription(event.description || "");
  };

  const handleEditMaxParticipantsChange = (value: string) => {
    setEditEventMaxParticipants(value);

    // Validate max participants
    if (value && parseInt(value) > 10000) {
      setEditEventMaxParticipantsError(t("community.create_post.max_participants_error"));
    } else if (value && parseInt(value) < 1) {
      setEditEventMaxParticipantsError(t("community.create_post.min_participants_error"));
    } else {
      setEditEventMaxParticipantsError("");
    }
  };

  const validateEditEventTimes = (startTime: string, endTime: string) => {
    if (startTime && endTime) {
      const [startHours, startMinutes] = startTime.split(":").map(Number);
      const [endHours, endMinutes] = endTime.split(":").map(Number);

      const startInMinutes = startHours * 60 + startMinutes;
      const endInMinutes = endHours * 60 + endMinutes;

      if (startInMinutes >= endInMinutes) {
        setEditEventTimeError(t("community.create_post.end_time_after_start"));
        return false;
      } else {
        setEditEventTimeError("");
        return true;
      }
    }
    setEditEventTimeError("");
    return true;
  };

  const handleEditEventTimeChange = (value: string) => {
    setEditEventTime(value);
    validateEditEventTimes(value, editEventEndTime);
  };

  const handleEditEventEndTimeChange = (value: string) => {
    setEditEventEndTime(value);
    validateEditEventTimes(editEventTime, value);
  };

  const handleSaveEdit = async (eventId: string) => {
    if (!editEventTitle.trim() || !editEventDate || !editEventTime || !editEventLocation.trim()) {
      toast({
        title: t("community.events.missing_fields"),
        description: t("community.events.missing_fields_desc"),
        variant: "destructive",
      });
      return;
    }

    // Validate max participants if provided
    if (
      editEventMaxParticipants &&
      (parseInt(editEventMaxParticipants) > 10000 || parseInt(editEventMaxParticipants) < 1)
    ) {
      toast({
        title: t("community.events.invalid_max_participants"),
        description: t("community.events.invalid_max_participants_desc"),
        variant: "destructive",
      });
      return;
    }

    // Validate event times
    if (editEventEndTime && !validateEditEventTimes(editEventTime, editEventEndTime)) {
      toast({
        title: t("community.events.invalid_times"),
        description: t("community.events.invalid_times_desc"),
        variant: "destructive",
      });
      return;
    }

    const [hours, minutes] = editEventTime.split(":");
    const eventDateTime = new Date(editEventDate);
    eventDateTime.setHours(parseInt(hours), parseInt(minutes));

    let eventEndDateTime: Date | undefined;
    if (editEventEndTime) {
      const [endHours, endMinutes] = editEventEndTime.split(":");
      eventEndDateTime = new Date(editEventDate);
      eventEndDateTime.setHours(parseInt(endHours), parseInt(endMinutes));
    }

    const eventDetails = {
      title: editEventTitle,
      date: eventDateTime,
      endDate: eventEndDateTime,
      location: editEventLocation,
      maxParticipants: editEventMaxParticipants ? parseInt(editEventMaxParticipants) : undefined,
      description: editEventDescription || undefined,
    };

    try {
      // The parent performs the API call and owns the success/failure toast.
      // Awaiting it means a rejected save no longer reports success.
      await onEditEvent?.(eventId, eventDetails);
    } catch {
      // Keep the edit form open so the change isn't lost; parent already toasted.
      return;
    }

    // Only reflect the edit locally once the save actually succeeded.
    setEvents(
      events.map((event) =>
        event.id === eventId
          ? {
              ...event,
              ...eventDetails,
            }
          : event,
      ),
    );

    setEditingEventId(null);
  };

  const handleCancelEdit = () => {
    setEditingEventId(null);
  };

  const handleCancelEvent = (eventId: string) => {
    const event = events.find((e) => e.id === eventId);

    trackEvent("community_events_page_cancel", {
      event_id: eventId,
      participant_count: event?.participants.length || 0,
      from: "events_page",
    });

    setEvents(
      events.map((event) =>
        event.id === eventId ? { ...event, status: "cancelled" as const } : event,
      ),
    );

    // Call parent callback to update feed
    onCancelEvent?.(eventId);

    setCancelEventId(null);
    toast({
      title: t("community.events.event_cancelled"),
      description: t("community.events.event_cancelled_desc"),
    });
  };

  const handleLeaveEvent = () => {
    if (!unregisterEventId) return;

    const eventId = unregisterEventId;
    const event = events.find((e) => e.id === eventId);

    trackEvent("community_events_page_leave", {
      event_id: eventId,
      participant_count: event?.participants.length || 0,
      from: "events_page",
    });

    // Remove from attending events list immediately
    setAttendingEventsFromAPI((prevEvents) => prevEvents.filter((event) => event.id !== eventId));

    // Update local events state
    setEvents(
      events.map((event) =>
        event.id === eventId
          ? {
              ...event,
              participants: event.participants.filter((p) => p.userId !== currentUserId),
            }
          : event,
      ),
    );

    // Call parent callback to update feed
    onLeaveEvent?.(eventId);

    setUnregisterEventId(null);
    toast({
      title: t("community.events.left_event"),
      description: t("community.events.left_event_desc"),
    });
  };

  const handleJoinEventLocal = (eventId: string) => {
    // Check if in recommended events
    const recommendedEvent = recommended.find((e) => e.id === eventId);

    if (recommendedEvent) {
      trackEvent("community_events_page_join", {
        event_id: eventId,
        participant_count: recommendedEvent.participants.length,
        max_participants: recommendedEvent.maxParticipants,
        from: "events_page",
      });

      // Add to main events list
      const updatedEvent = {
        ...recommendedEvent,
        participants: [
          ...recommendedEvent.participants,
          {
            userId: currentUserId,
            userName: "Current User",
            userTitle: "Construction Worker",
            registeredAt: new Date(),
          },
        ],
      };

      // Add to attending events list immediately
      setAttendingEventsFromAPI((prevEvents) => [...prevEvents, updatedEvent]);

      setEvents([...events, updatedEvent]);
      setRecommended(recommended.filter((e) => e.id !== eventId));

      // Call parent callback to update feed
      onJoinEvent?.(eventId);

      toast({
        title: t("community.events.joined_event"),
        description: t("community.events.joined_event_desc"),
      });
    }
  };

  const toggleExpandEvent = (eventId: string) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  const handleShareToFeed = async (eventId: string) => {
    try {
      trackEvent("community_event_shared", {
        event_id: eventId,
        share_method: "feed",
        from: "events_page",
      });

      await sharePost(eventId);

      navigate(getCommunityPath(user?.type));

      toast({
        title: t("community.events.shared_to_feed"),
        description: t("community.events.shared_to_feed_desc"),
      });
    } catch (error) {
      console.error("Error sharing event:", error);
      toast({
        title: t("community.events.failed_share"),
        description: t("community.events.failed_share_desc"),
        variant: "destructive",
      });
    }
  };

  const handleShareToLinkedIn = (eventId: string) => {
    trackEvent("community_event_shared", {
      event_id: eventId,
      share_method: "linkedin",
      from: "events_page",
    });

    const userType = user?.type === "employer" ? "employer" : "jobseeker";
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const eventUrl = `${baseUrl}/${userType}/event/${eventId}`;
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(eventUrl)}`;
    window.open(linkedInUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareToWhatsApp = (eventId: string) => {
    trackEvent("community_event_shared", {
      event_id: eventId,
      share_method: "whatsapp",
      from: "events_page",
    });

    const userType = user?.type === "employer" ? "employer" : "jobseeker";
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const eventUrl = `${baseUrl}/${userType}/event/${eventId}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(eventUrl)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const renderEventCard = (
    event: EventData,
    showActions: "creator" | "attendee" | "recommended",
    isReported: boolean = false,
  ) => {
    const isExpanded = expandedEventId === event.id;
    const isEditing = editingEventId === event.id;

    return (
      <Card
        key={event.id}
        className={`${
          event.status === "cancelled"
            ? "border-destructive/20 bg-destructive/5"
            : isReported
              ? "border-amber-200 bg-amber-50/40"
              : "border-border bg-card"
        }`}
      >
        {isEditing ? (
          // Edit Mode
          <>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent gap-1"
                >
                  <Edit2 className="h-3 w-3" />
                  {t("community.events.editing_event")}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold">
                  {t("community.events.event_title")} <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={editEventTitle}
                  onChange={(e) => setEditEventTitle(e.target.value)}
                  placeholder={t("community.events.event_title_placeholder")}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold">
                    {t("community.events.date")} <span className="text-destructive">*</span>
                  </label>
                  <ShadCNDatePicker
                    value={editEventDate}
                    onChange={setEditEventDate}
                    placeholder={t("community.events.pick_date")}
                    disablePastDates
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold">
                    {t("community.events.max_participants_optional")}
                  </label>
                  <Input
                    type="number"
                    value={editEventMaxParticipants}
                    onChange={(e) => handleEditMaxParticipantsChange(e.target.value)}
                    placeholder={t("community.events.max_participants_placeholder")}
                    min="1"
                    max="10000"
                    className={`w-full ${editEventMaxParticipantsError ? "border-destructive" : ""}`}
                  />
                  {editEventMaxParticipantsError && (
                    <p className="text-destructive mt-1 text-xs">{editEventMaxParticipantsError}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-semibold">
                    {t("community.events.start_time")} <span className="text-destructive">*</span>
                  </label>
                  <TimeInput value={editEventTime} onChange={handleEditEventTimeChange} />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold">
                    {t("community.events.end_time")}
                  </label>
                  <TimeInput value={editEventEndTime} onChange={handleEditEventEndTimeChange} />
                  {editEventTimeError && (
                    <p className="text-destructive mt-1 text-xs">{editEventTimeError}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold">
                  {t("community.events.location")} <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={editEventLocation}
                  onChange={(e) => setEditEventLocation(e.target.value)}
                  placeholder={t("community.events.location_placeholder")}
                  className="w-full"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-semibold">
                  {t("community.events.description_optional")}
                </label>
                <Tiptap
                  ref={editDescriptionEditorRef}
                  content={editEventDescription}
                  onChange={setEditEventDescription}
                  placeholder={t("community.events.description_placeholder")}
                  enableLink={true}
                  enableAutolink={true}
                  enableMention={false}
                  enableEmoji={true}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleSaveEdit(event.id)} size="sm" className="gap-2">
                  <UserCheck className="h-4 w-4" />
                  {t("community.events.save_changes")}
                </Button>
                <Button onClick={handleCancelEdit} size="sm" variant="ghost" className="gap-2">
                  <X className="h-4 w-4" />
                  {t("community.events.cancel")}
                </Button>
              </div>
            </CardContent>
          </>
        ) : (
          // Display Mode
          <>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <div className="flex min-w-0 items-start gap-2">
                      <Calendar className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                      <h3 className="text-lg leading-snug font-bold">{event.title}</h3>
                    </div>
                    {event.isEdited && (
                      <Badge variant="secondary" className="gap-1 text-xs">
                        {t("community.events.updated_badge")}
                      </Badge>
                    )}
                    {isReported && (
                      <>
                        <Badge
                          variant="outline"
                          className="border-destructive/30 bg-destructive/10 text-destructive gap-1"
                        >
                          <EyeOff className="h-3 w-3" />
                          {t("community.events.hidden_from_feed")}
                        </Badge>
                        {event.status !== "cancelled" && (
                          <Badge
                            variant="outline"
                            className="gap-1 border-amber-200 bg-amber-50 text-amber-700"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {t("community.events.action_required")}
                          </Badge>
                        )}
                      </>
                    )}
                    {event.status === "cancelled" && (
                      <Badge variant="destructive" className="gap-1">
                        <X className="h-3 w-3" />
                        {t("community.events.cancelled_badge")}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {t("community.events.created_by")}{" "}
                    <span
                      className="text-foreground cursor-pointer font-medium hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUserProfileClick(
                          event.createdBy.userId,
                          event.createdBy.userType,
                          event.createdBy.organizationId,
                        );
                      }}
                    >
                      {event.createdBy.userName}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="secondary"
                    className="border-brand-accent-border bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent gap-1"
                  >
                    <Users className="h-3 w-3" />
                    {event.participants.length}
                    {event.maxParticipants && `/${event.maxParticipants}`}
                  </Badge>
                  <Popover
                    open={shareMenuOpenEventId === event.id}
                    onOpenChange={(open) => setShareMenuOpenEventId(open ? event.id : null)}
                  >
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 rounded-xl p-1.5 shadow-lg" align="end">
                      <div className="space-y-1">
                        <button
                          onClick={() => {
                            handleShareToFeed(event.id);
                            setShareMenuOpenEventId(null);
                          }}
                          className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                        >
                          <Rss className="text-primary h-4 w-4" />
                          <span className="font-medium">{t("community.events.share_to_feed")}</span>
                        </button>
                        <button
                          onClick={() => {
                            handleShareToLinkedIn(event.id);
                            setShareMenuOpenEventId(null);
                          }}
                          className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                        >
                          <Linkedin className="h-4 w-4 text-[#0A66C2]" />
                          <span className="font-medium">
                            {t("community.events.share_to_linkedin")}
                          </span>
                        </button>
                        <button
                          onClick={() => {
                            handleShareToWhatsApp(event.id);
                            setShareMenuOpenEventId(null);
                          }}
                          className="hover:bg-accent flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                        >
                          <Send className="h-4 w-4 text-[#25D366]" />
                          <span className="font-medium">
                            {t("community.events.share_to_whatsapp")}
                          </span>
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {showActions !== "creator" && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                      onClick={() => setReportEventId(event.id)}
                    >
                      <Flag className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3">
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="text-muted-foreground h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-medium">
                      {event.date.toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <p className="text-muted-foreground">
                      {event.date.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      {event.endDate && (
                        <>
                          {" "}
                          -{" "}
                          {event.endDate.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                  <p className="font-medium">{event.location}</p>
                </div>
                {event.description && (
                  <div
                    className="text-muted-foreground border-t pt-2 text-sm"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
                  />
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-3">
                {showActions === "creator" &&
                  event.status !== "cancelled" &&
                  (isReported ? (
                    <Button
                      onClick={() =>
                        navigate(
                          `${getCommunityPath(user?.type)}?section=support&tab=reports-on-my-content&reportId=${event.id}`,
                        )
                      }
                      variant="outline"
                      className="w-full gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
                    >
                      <ExternalLink className="h-4 w-4" />
                      {t("community.events.edit_in_support")}
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditEvent(event)}
                        variant="outline"
                        className="flex-1 gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        {t("community.events.edit_event")}
                      </Button>
                      <Button
                        onClick={() => setCancelEventId(event.id)}
                        variant="destructive"
                        className="flex-1 gap-2"
                      >
                        <X className="h-4 w-4" />
                        {t("community.events.cancel_event")}
                      </Button>
                    </div>
                  ))}

                {showActions === "attendee" && event.status !== "cancelled" && (
                  <Button
                    onClick={() => setUnregisterEventId(event.id)}
                    variant="outline"
                    className="border-destructive text-destructive hover:bg-destructive/10 w-full gap-2"
                  >
                    <UserMinus className="h-4 w-4" />
                    {t("community.events.unregister_from_event")}
                  </Button>
                )}

                {showActions === "recommended" && (
                  <Button
                    onClick={() => handleJoinEventLocal(event.id)}
                    className="bg-primary hover:bg-primary/90 w-full gap-2"
                  >
                    <UserCheck className="h-4 w-4" />
                    {t("community.events.join")}
                  </Button>
                )}

                {/* Show Attendees */}
                {event.participants.length > 0 && (
                  <div className="border-t pt-3">
                    <Button
                      variant={event.status === "cancelled" ? "destructive" : "ghost"}
                      onClick={() => toggleExpandEvent(event.id)}
                      className="w-full justify-between"
                    >
                      <span className="text-sm font-semibold">
                        {t("community.events.attendees")} ({event.participants.length})
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>

                    {isExpanded && (
                      <div className="mt-3 space-y-2">
                        {event.participants.map((participant, index) => (
                          <div
                            key={index}
                            className={`flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors ${
                              event.status === "cancelled"
                                ? "bg-destructive/10 hover:bg-destructive/20"
                                : "bg-muted/50 hover:bg-muted"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUserProfileClick(
                                participant.userId,
                                participant.userType,
                                participant.organizationId,
                              );
                            }}
                          >
                            <Avatar className="ring-border h-8 w-8 ring-2">
                              <AvatarImage src={participant.photoUrl} alt={participant.userName} />
                              <AvatarFallback className="bg-brand-accent text-primary text-xs font-semibold">
                                {participant.userName
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{participant.userName}</p>
                              <p className="text-muted-foreground truncate text-xs">
                                {participant.userTitle}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="my-events" className="space-y-6">
        <TabsList className="border-border h-auto w-full [scrollbar-width:none] justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0 [&::-webkit-scrollbar]:hidden">
          <TabsTrigger
            value="my-events"
            className="text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary relative -mb-px shrink-0 gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3 py-3 text-sm font-semibold shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            <Calendar className="h-4 w-4" />
            {t("community.events.my_events")}
          </TabsTrigger>
          <TabsTrigger
            value="attending"
            className="text-muted-foreground hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary relative -mb-px shrink-0 gap-2 rounded-none border-b-2 border-transparent bg-transparent px-3 py-3 text-sm font-semibold shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:shadow-none"
          >
            <UserCheck className="h-4 w-4" />
            {t("community.events.attending")}
          </TabsTrigger>
        </TabsList>

        {/* My Events Tab */}
        <TabsContent value="my-events" className="mt-0 space-y-6">
          {isLoadingMyEvents ? (
            <>
              {Array.from({ length: 3 }).map((_, index) => (
                <EventSkeleton key={`my-event-skeleton-${index}`} />
              ))}
            </>
          ) : myEvents.length === 0 ? (
            <Card className="border-border">
              <div className="flex flex-col items-center gap-3 py-14 text-center">
                <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                  <Calendar className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-foreground text-base font-semibold">
                    {t("community.events.no_events")}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t("community.events.no_events_desc")}
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <>
              {/* Reported Events Section */}
              {reportedEvents.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Flag className="h-5 w-5 text-amber-700" />
                    <h3 className="text-lg font-semibold text-amber-700">
                      {t("community.events.reported_events")} ({reportedEvents.length})
                    </h3>
                  </div>
                  <Alert variant="default" className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <AlertDescription className="text-amber-700">
                      {t("community.events.reported_events_alert")} <br />
                      <span
                        className="cursor-pointer font-semibold text-amber-700 hover:underline"
                        onClick={() => navigate(`${getCommunityPath(user?.type)}?section=support`)}
                      >
                        {t("community.events.go_to_support")}
                      </span>{" "}
                      {t("community.events.reported_events_alert_suffix")}
                    </AlertDescription>
                  </Alert>
                  {reportedEvents.map((event) => renderEventCard(event, "creator", true))}
                </div>
              )}

              {/* Active Events Section */}
              <div className="space-y-4">
                {reportedEvents.length > 0 && (
                  <h3 className="text-lg font-semibold">
                    {t("community.events.active_events")} ({activeEvents.length})
                  </h3>
                )}
                {activeEvents.length === 0 && reportedEvents.length > 0 ? (
                  <Card className="border-border">
                    <div className="flex flex-col items-center gap-3 py-14 text-center">
                      <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                        <Calendar className="h-6 w-6" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-foreground text-base font-semibold">
                          {t("community.events.no_active_events")}
                        </p>
                      </div>
                    </div>
                  </Card>
                ) : (
                  activeEvents.map((event) => renderEventCard(event, "creator"))
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* Attending Tab */}
        <TabsContent value="attending" className="mt-0 space-y-6">
          {/* Attending Events */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              {t("community.events.events_attending_title")}
            </h3>
            {isLoadingAttending ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <EventSkeleton key={`attending-skeleton-${index}`} />
                ))}
              </>
            ) : attendingEvents.length === 0 ? (
              <Card className="border-border">
                <div className="flex flex-col items-center gap-3 py-14 text-center">
                  <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                    <UserCheck className="h-6 w-6" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-foreground text-base font-semibold">
                      {t("community.events.no_attending")}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {t("community.events.no_attending_desc")}
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              attendingEvents.map((event) => renderEventCard(event, "attendee"))
            )}
          </div>

          {/* Recommended Events */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-5 w-5" />
              <h3 className="text-lg font-semibold">{t("community.events.recommended")}</h3>
            </div>
            {isLoadingRecommended ? (
              <>
                {Array.from({ length: 3 }).map((_, index) => (
                  <EventSkeleton key={`recommended-skeleton-${index}`} />
                ))}
              </>
            ) : recommended.filter((event) => event?.createdBy?.userId !== user?.id).length ===
              0 ? (
              <Card className="border-border">
                <div className="flex flex-col items-center gap-3 py-14 text-center">
                  <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                    <Sparkles className="h-6 w-6" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-foreground text-base font-semibold">
                      {t("community.events.no_recommended")}
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              recommended
                .filter((event) => event.createdBy.userId !== user?.id)
                .map((event) => renderEventCard(event, "recommended"))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Cancel Event Dialog */}
      <AlertDialog open={cancelEventId !== null} onOpenChange={() => setCancelEventId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("community.events.cancel_event_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("community.events.cancel_event_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("community.comments.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelEventId && handleCancelEvent(cancelEventId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("community.events.cancel_event")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unregister Event Dialog */}
      <AlertDialog
        open={unregisterEventId !== null}
        onOpenChange={() => setUnregisterEventId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("community.events.leave_event_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("community.events.leave_event_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("community.comments.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleLeaveEvent}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("community.events.leave")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Report Event Dialog */}
      {reportEventId && (
        <ReportDialog
          open={!!reportEventId}
          onOpenChange={(open) => {
            if (!open) setReportEventId(null);
          }}
          targetId={reportEventId}
          targetType="event"
        />
      )}
    </div>
  );
}
